import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, writeBatch, onSnapshot, arrayUnion } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, CheckCircle, Package, Loader2, Save, FileText, X, ChevronDown, RefreshCw, AlertCircle, AlertTriangle, Clock, Printer } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import MOCPreviewContent from './MOCPreviewContent.js';
import { isWarehouseUser } from './accessControl.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import { updateDispatchAudit } from './utils/dispatchAudit.js';

const getPackingProofFiles = (mocData = {}) => {
  if (Array.isArray(mocData.packingProofFiles)) return mocData.packingProofFiles;
  if (!mocData.packingImageUrl) return [];

  return [{
    name: 'Final Packing Image',
    url: mocData.packingImageUrl,
    uploadedAt: mocData.packedAt || mocData.updatedAt || '',
    type: 'image'
  }];
};

const PackingDetail = ({ db, user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [moc, setMoc] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [packingProofFiles, setPackingProofFiles] = useState([]);
  const [showPackingDoneModal, setShowPackingDoneModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const moreDropdownRef = useRef(null);
  const printRef = useRef();
  const isWarehouseRole = isWarehouseUser(user);

  useClickOutside(moreDropdownRef, () => setShowMore(false));

  useEffect(() => {
    const fetchMoc = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "dispatches", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const mocData = { id: docSnap.id, ...docSnap.data() };
          setMoc(mocData);
          setPackingProofFiles(getPackingProofFiles(mocData));

          if (mocData.orderId) {
            const orderSnap = await getDoc(doc(db, "orders", mocData.orderId));
            if (orderSnap.exists()) {
              setOrder({ id: orderSnap.id, ...orderSnap.data() });
            }
          }
        } else {
          alert("MOC not found");
          navigate('/ops/packing');
        }
      } catch (error) {
        console.error("Error fetching MOC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMoc();
  }, [db, id, navigate]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!moc?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", moc.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, moc?.orderId]);

  const handleSave = async () => {
    const confirmed = window.confirm("Are you sure you want to update this packing schedule?");
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      const docRef = doc(db, "dispatches", id);
      const newSeq = parseInt(moc.sequence) || 0;
      const targetDate = moc.scheduledDate || '';
      const timestamp = serverTimestamp();
      const auditTimestamp = new Date().toISOString();
      const wasPreviouslyScheduled = Boolean(moc.packingScheduledAt || moc.packingStatus === 'packing_scheduled');
      
      const updates = {
        sequence: newSeq,
        priority: moc.priority || 'Medium',
        warehouseRemarks: moc.warehouseRemarks || '',
        scheduledDate: targetDate,
        updatedAt: timestamp
      };

      // Auto-transition to the packing schedule when sequence and priority exist.
      // The packing lifecycle now lives in packingStatus, while dispatchStatus moves to dc_pending.
      if (newSeq > 0 && moc.priority && (moc.dispatchStatus === 'packing_queued' || moc.dispatchStatus === 'packing_hold' || moc.dispatchStatus === 'dc_pending' || !moc.dispatchStatus)) {
        updates.packingStatus = 'packing_scheduled';
        updates.dispatchStatus = 'dc_pending';
        updates.packingScheduledAt = timestamp;
        updates.packingHoldReason = ''; // Clear hold reason
        
        if (moc.orderId) {
          const orderRef = doc(db, "orders", moc.orderId);
          await updateDoc(orderRef, { 
            status: 'packing_scheduled',
            packingHoldReason: '',
            updatedAt: timestamp
          });
        }
      }

      // Auto-increment logic for conflicting sequences on the same day
      if (newSeq > 0 && targetDate && ['packing_queued', 'dc_pending', 'packing_scheduled', 'packing', 'packing_hold'].includes(moc.dispatchStatus || 'packing_queued')) {
          const q = query(
              collection(db, "dispatches"),
              where("dispatchStatus", "in", ["packing_queued", "dc_pending", "packing_scheduled", "packing"]),
              where("scheduledDate", "==", targetDate)
          );
          const querySnapshot = await getDocs(q);
          const batch = writeBatch(db);
          
          let conflicts = [];
          querySnapshot.forEach(docSnap => {
              if (docSnap.id !== id) {
                  const d = docSnap.data();
                  if (d.sequence >= newSeq) {
                      conflicts.push({ id: docSnap.id, sequence: d.sequence });
                  }
              }
          });
          
          conflicts.sort((a, b) => a.sequence - b.sequence);
          
          let expectedSeq = newSeq;
          for (let i = 0; i < conflicts.length; i++) {
              if (conflicts[i].sequence === expectedSeq) {
                  expectedSeq++;
                  batch.update(doc(db, "dispatches", conflicts[i].id), { sequence: expectedSeq });
              }
          }
          await batch.commit();
      }

      const historyEntry = {
        sequence: newSeq,
        priority: updates.priority,
        warehouseRemarks: updates.warehouseRemarks,
        scheduledDate: targetDate,
        packingStatusBefore: moc.packingStatus || '',
        packingStatusAfter: updates.packingStatus || moc.packingStatus || '',
        dispatchStatusBefore: moc.dispatchStatus || 'packing_queued',
        dispatchStatusAfter: updates.dispatchStatus || moc.dispatchStatus || 'packing_queued',
        changedByEmail: user?.email || '',
        changedByName: user?.displayName || user?.email || '',
        changedByRole: user?.role || '',
        changedAt: new Date().toISOString()
      };

      await updateDoc(docRef, {
        ...updates,
        packingScheduleHistory: arrayUnion(historyEntry)
      });
      await updateDispatchAudit(db, {
        dispatchId: id,
        dispatchData: {
          ...moc,
          ...updates,
          dispatchStatus: updates.dispatchStatus || moc.dispatchStatus || 'packing_queued'
        },
        user,
        historyEvent: {
          action: wasPreviouslyScheduled ? 'packing_schedule_updated' : 'packing_scheduled',
          dispatchStatus: updates.dispatchStatus || moc.dispatchStatus || 'packing_queued',
          timestamp: auditTimestamp,
          remarks: updates.warehouseRemarks || ''
        }
      });
      setMoc(prev => ({
        ...prev,
        ...updates,
        packingScheduleHistory: [...(prev?.packingScheduleHistory || []), historyEntry]
      }));
      alert("Packing details saved successfully!");
      navigate('/ops/packing');
    } catch (error) {
      alert("Error saving: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePackingDoneClick = () => {
    setShowPackingDoneModal(true);
  };

  const uploadPackingProofFiles = async () => {
    const existingFiles = packingProofFiles
      .filter(file => file.url)
      .map(file => ({
        name: file.name || 'Packing proof',
        url: file.url,
        size: file.size || 0,
        type: file.type || '',
        uploadedAt: file.uploadedAt || ''
      }));

    const newFiles = packingProofFiles.filter(file => !file.url);
    if (newFiles.length === 0) return existingFiles;

    setUploading(true);
    try {
      const uploadedFiles = await Promise.all(newFiles.map(async (file, index) => {
        const path = `packing_images/${id}/${Date.now()}_${index}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        return {
          name: file.name,
          url,
          size: file.size || 0,
          type: file.type || '',
          uploadedAt: new Date().toISOString()
        };
      }));

      return [...existingFiles, ...uploadedFiles];
    } finally {
      setUploading(false);
    }
  };

  const handlePackingDoneConfirm = async () => {
    setIsUpdating(true);
    try {
      const docRef = doc(db, "dispatches", id);
      const timestamp = serverTimestamp();
      const auditTimestamp = new Date().toISOString();
      const uploadedPackingProofFiles = await uploadPackingProofFiles();
      const updates = {
        packingStatus: 'packed_ready',
        sequence: 0, // Remove from sequence
        packingRemarks: moc.packingRemarks || '',
        packingProofFiles: uploadedPackingProofFiles,
        packingImageUrl: uploadedPackingProofFiles[0]?.url || '',
        packedAt: timestamp,
        updatedAt: timestamp
      };

      await updateDoc(docRef, updates);
      await updateDispatchAudit(db, {
        dispatchId: id,
        dispatchData: {
          ...moc,
          ...updates,
          dispatchStatus: moc.dispatchStatus || 'dc_pending'
        },
        user,
        historyEvent: {
          action: 'packed_ready',
          dispatchStatus: moc.dispatchStatus || 'dc_pending',
          timestamp: auditTimestamp,
          remarks: updates.packingRemarks || ''
        }
      });

      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: 'packed_ready',
          updatedAt: timestamp
        });
      }

      setMoc(prev => ({ ...prev, ...updates }));
      setPackingProofFiles(uploadedPackingProofFiles);
      alert("Packing Complete! Record locked and moved to DC stage.");
      setShowPackingDoneModal(false);
      navigate('/ops/ready-to-dispatch');
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnschedule = async () => {
    if (!window.confirm("Remove this item from the active schedule? It will remain in the queue but lose its sequence and date.")) return;
    setIsUpdating(true);
    try {
      const docRef = doc(db, "dispatches", id);
      const resetData = {
        dispatchStatus: 'packing_queued',
        packingStatus: '',
        sequence: 0,
        scheduledDate: '',
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(docRef, resetData);

      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), { 
            status: 'in_que', 
            updatedAt: serverTimestamp() 
        });
      }

      setMoc(prev => ({ ...prev, ...resetData }));
      alert("Item Unscheduled.");
      setShowMore(false);
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleHold = async () => {
    if (!holdReason.trim()) return alert("Please provide a reason for the hold.");
    setIsUpdating(true);
    try {
      const docRef = doc(db, "dispatches", id);
      const timestamp = serverTimestamp();
      const updates = {
        dispatchStatus: 'packing_hold',
        sequence: 0,
        packingHoldReason: holdReason,
        updatedAt: timestamp
      };

      await updateDoc(docRef, updates);

      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), { 
            status: 'packing_hold',
            packingHoldReason: holdReason,
            updatedAt: timestamp 
        });
      }

      setMoc(prev => ({ ...prev, ...updates }));
      alert("MOC placed on Hold.");
      setShowHoldModal(false);
      setShowMore(false);
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return alert("Please provide a reason for the rejection.");
    setIsUpdating(true);
    try {
      const docRef = doc(db, "dispatches", id);
      const timestamp = serverTimestamp();
      const updates = {
        dispatchStatus: 'packing_rejected',
        sequence: 0,
        scheduledDate: '',
        packingRemarks: '',
        packingRejectionReason: rejectReason,
        updatedAt: timestamp
      };

      await updateDoc(docRef, updates);

      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), { 
            status: 'packing_rejected',
            packingRejectionReason: rejectReason,
            updatedAt: timestamp 
        });
      }

      alert("MOC Rejected and removed from queue.");
      navigate('/ops/packing');
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '--';
    const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(d)) return '--';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return d.toLocaleDateString('en-IN', options);
  };
  
  const formatOnlyDate = (dateString) => {
    if (!dateString) return '--';
    if (typeof dateString === 'string' && dateString.length === 10) {
      return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    const d = new Date(dateString);
    if (isNaN(d)) return '--';
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleExportPDF = () => {
    const printContent = printRef.current?.innerHTML;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOC ${moc?.mocNo || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const isLocked = moc && (moc.packingStatus === 'packed_ready' || moc.dispatchStatus === 'packed_ready' || moc.dispatchStatus === 'packing_rejected');
  const latestPackingScheduleEntry = Array.isArray(moc?.packingScheduleHistory) && moc.packingScheduleHistory.length > 0
    ? moc.packingScheduleHistory[moc.packingScheduleHistory.length - 1]
    : null;

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/packing')} style={backBtnStyle}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0 }}>Packing Execution: {moc.mocNo}</h2>
          {isLocked && <span style={lockedBadgeStyle}><CheckCircle size={14} /> RECORD LOCKED</span>}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {!isLocked && (
                <>
                    {!isWarehouseRole && (
                        <button onClick={handleSave} disabled={isUpdating} style={saveBtnStyle}>
                            <Save size={18} /> Save Schedule
                        </button>
                    )}
                    {isWarehouseRole && (
                        <button onClick={handlePackingDoneClick} disabled={isUpdating} style={confirmBtnStyle}>
                            <Package size={18} /> Packing Done
                        </button>
                    )}

                    {!isWarehouseRole && (
                        <div style={{ position: 'relative' }} ref={moreDropdownRef}>
                            <button 
                                onClick={() => setShowMore(!showMore)} 
                                style={{...cancelBtnStyle, backgroundColor: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0'}}
                            >
                                More <ChevronDown size={16} />
                            </button>
                            {showMore && (
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: '5px',
                                    backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '160px', overflow: 'hidden'
                                }}>
                                    <button 
                                        onClick={handleUnschedule}
                                        style={{
                                            width: '100%', padding: '10px 15px', textAlign: 'left', background: 'none', border: 'none',
                                            cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', color: '#4b5563'
                                        }}
                                        onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                    >
                                        <RefreshCw size={14} /> Unschedule
                                    </button>
                                    <button 
                                        onClick={() => { setShowHoldModal(true); setShowMore(false); }}
                                        style={{
                                            width: '100%', padding: '10px 15px', textAlign: 'left', background: 'none', border: 'none',
                                            cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', color: '#f59e0b'
                                        }}
                                        onMouseOver={(e) => e.target.style.backgroundColor = '#fffbeb'}
                                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                    >
                                        <Clock size={14} /> Place on Hold
                                    </button>
                                    <button 
                                        onClick={() => { setShowRejectModal(true); setShowMore(false); }}
                                        style={{
                                            width: '100%', padding: '10px 15px', textAlign: 'left', background: 'none', border: 'none',
                                            cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626'
                                        }}
                                        onMouseOver={(e) => e.target.style.backgroundColor = '#fef2f2'}
                                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                    >
                                        <AlertTriangle size={14} /> Reject MOC
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
            {isLocked && (
                <button onClick={() => navigate('/ops/ready-to-dispatch')} style={saveBtnStyle}>
                    Go to DC Stage
                </button>
            )}
            <button onClick={handleExportPDF} style={secondaryBtnStyle}>
                <Printer size={18} /> Export PDF
            </button>
        </div>
      </div>

      {/* Status Banners */}
      {moc.dispatchStatus === 'packing_hold' && (
        <div style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '12px' }}>
          <Clock color="#faad14" size={20} />
          <div>
            <div style={{ fontWeight: 'bold', color: '#856404' }}>Packing On Hold</div>
            <div style={{ fontSize: '13px', color: '#856404' }}>Reason: {moc.packingHoldReason}</div>
          </div>
        </div>
      )}

      {moc.dispatchStatus === 'packing_rejected' && (
        <div style={{ backgroundColor: '#fff1f0', border: '1px solid #ffa39e', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '12px' }}>
          <AlertCircle color="#f5222d" size={20} />
          <div>
            <div style={{ fontWeight: 'bold', color: '#cf1322' }}>Packing Rejected</div>
            <div style={{ fontSize: '13px', color: '#cf1322' }}>Reason: {moc.packingRejectionReason}</div>
          </div>
        </div>
      )}

      {/* 2. SCHEDULING FORM / PERFORMANCE SUMMARY */}
      <div style={{ ...cardStyle, marginBottom: '30px', border: isLocked ? '1px solid #e5e7eb' : '2px solid #2563eb' }}>
          <h3 style={{ ...sectionHeaderStyle, color: isLocked ? '#64748b' : '#2563eb', borderBottom: '1px solid #dbeafe', marginBottom: '20px' }}>
            <FileText size={20} /> {isLocked ? 'Packing Performance Summary' : isWarehouseRole ? 'Packing Schedule' : 'Execution & Scheduling'}
          </h3>
          
          {isLocked ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div>
                          <label style={labelStyle}>Status</label>
                          <div style={{ fontWeight: 'bold', color: '#059669', display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <CheckCircle size={16} /> Packing Done
                          </div>
                      </div>
                      <div>
                          <label style={labelStyle}>Original Schedule</label>
                          <div>{formatOnlyDate(moc.scheduledDate)}</div>
                      </div>
                      <div>
                          <label style={labelStyle}>Actually Packed On</label>
                          <div style={{ fontWeight: 'bold' }}>{formatDateTime(moc.packedAt)}</div>
                      </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div>
                          <label style={labelStyle}>Warehouse Remarks</label>
                          <div style={{ backgroundColor: '#f9fafb', padding: '10px', borderRadius: '6px', fontSize: '14px', border: '1px solid #e5e7eb', marginBottom: '10px' }}>
                              {moc.warehouseRemarks || 'No warehouse remarks.'}
                          </div>
                      </div>
                      <div>
                          <label style={labelStyle}>Packing Remarks</label>
                          <div style={{ backgroundColor: '#f9fafb', padding: '10px', borderRadius: '6px', minHeight: '60px', fontSize: '14px', border: '1px solid #e5e7eb' }}>
                              {moc.packingRemarks || 'No packing remarks provided.'}
                          </div>
                      </div>
                      <div>
                          <label style={labelStyle}>Final Packing Proofs</label>
                          {getPackingProofFiles(moc).length > 0 ? (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                  {getPackingProofFiles(moc).map((file, index) => (
                                      <a key={`${file.url}-${index}`} href={file.url} target="_blank" rel="noopener noreferrer" style={proofFileLinkStyle}>
                                          <FileText size={16} color="#2563eb" />
                                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {file.name || `Packing proof ${index + 1}`}
                                          </span>
                                      </a>
                                  ))}
                              </div>
                          ) : (
                              <div style={{ color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>No packing proofs uploaded.</div>
                          )}
                      </div>
                  </div>
              </div>
          ) : isWarehouseRole ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div style={scheduleSummaryGridStyle}>
                      <div style={scheduleInfoCardStyle}>
                          <label style={labelStyle}>Packing Sequence</label>
                          <div style={schedulePrimaryValueStyle}>{moc.sequence || '--'}</div>
                      </div>
                      <div style={scheduleInfoCardStyle}>
                          <label style={labelStyle}>Scheduled Date</label>
                          <div style={schedulePrimaryValueStyle}>{formatOnlyDate(moc.scheduledDate)}</div>
                      </div>
                      <div style={scheduleInfoCardStyle}>
                          <label style={labelStyle}>Priority</label>
                          <div style={priorityPillStyle(moc.priority)}>{moc.priority || 'Medium'}</div>
                      </div>
                      <div style={scheduleInfoCardStyle}>
                          <label style={labelStyle}>Scheduled At</label>
                          <div style={scheduleSecondaryValueStyle}>
                              {formatDateTime(moc.packingScheduledAt || latestPackingScheduleEntry?.changedAt)}
                          </div>
                      </div>
                  </div>

                  <div style={scheduleRemarksCardStyle}>
                      <div>
                          <label style={labelStyle}>Warehouse Remarks / Instructions</label>
                          <div style={{ fontSize: '14px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {moc.warehouseRemarks || 'No warehouse remarks added by ops yet.'}
                          </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                          Last updated by {latestPackingScheduleEntry?.changedByName || latestPackingScheduleEntry?.changedByEmail || 'Ops'} 
                          {latestPackingScheduleEntry?.changedAt ? ` on ${formatDateTime(latestPackingScheduleEntry.changedAt)}` : ''}
                      </div>
                  </div>
              </div>
          ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  <div>
                      <label style={labelStyle}>Packing Sequence</label>
                      <input 
                        type="number" 
                        value={moc.sequence || ''} 
                        onChange={(e) => setMoc({...moc, sequence: e.target.value})}
                        style={inputStyle} 
                        placeholder="e.g. 1, 2, 3..." 
                      />
                  </div>
                  <div>
                      <label style={labelStyle}>Priority</label>
                      <select 
                        value={moc.priority || 'Medium'} 
                        onChange={(e) => setMoc({...moc, priority: e.target.value})}
                        style={inputStyle}
                      >
                          <option value="Low">Low (Green)</option>
                          <option value="Medium">Medium (Yellow)</option>
                          <option value="High">High (Red)</option>
                      </select>
                  </div>
                  <div>
                      <label style={labelStyle}>Scheduled Date</label>
                      <input 
                        type="date" 
                        value={moc.scheduledDate || ''} 
                        onChange={(e) => setMoc({...moc, scheduledDate: e.target.value})}
                        style={inputStyle} 
                      />
                  </div>
                  <div style={{ gridColumn: 'span 3' }}>
                      <label style={labelStyle}>Warehouse Remarks / Issues</label>
                      <textarea 
                        value={moc.warehouseRemarks || ''} 
                        onChange={(e) => setMoc({...moc, warehouseRemarks: e.target.value})}
                        style={{ ...inputStyle, minHeight: '80px' }} 
                        placeholder="Add notes about damage, missing items, or packing condition..." 
                      />
                  </div>
              </div>
          )}
      </div>


      {/* 1. MOC PREVIEW AREA */}
      <div style={previewContainerStyle}>
        <h3 style={{...sectionHeaderStyle, padding:'0 20px', paddingTop:'20px'}}>Material Breakdown</h3>
        <MOCPreviewContent
          mocData={moc}
          printRef={printRef}
          showFinanceSection={false}
          showPostInstructionsSection={false}
          className="packing-preview-area"
          style={pdfContainerStyle}
        />
      </div>

      
      {/* PACKING DONE MODAL */}
      {showPackingDoneModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '500px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#059669' }}>Complete Packing</h3>
              <button onClick={() => setShowPackingDoneModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Are you sure packing is complete? You can optionally upload a final packing photo and add any remarks.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
              <div>
                  <label style={labelStyle}>Final Packing Image / Files (Optional)</label>
                  <MultiFileUpload
                    value={packingProofFiles}
                    onFilesChange={setPackingProofFiles}
                    multiple={true}
                    maxSizeMB={25}
                    helperText="Upload one or more final packing proof files. Total size limit is 25MB."
                    uploading={uploading}
                  />
              </div>

              <div>
                  <label style={labelStyle}>Packing Remarks (Optional)</label>
                  <textarea 
                    value={moc.packingRemarks || ''} 
                    onChange={(e) => setMoc({...moc, packingRemarks: e.target.value})}
                    style={{ ...inputStyle, minHeight: '80px' }} 
                    placeholder="Add notes about damage, missing items, or packing condition..." 
                  />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowPackingDoneModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={handlePackingDoneConfirm} disabled={isUpdating || uploading} style={confirmBtnStyle}>
                {isUpdating ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Packing Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOLD MODAL */}
      {showHoldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '450px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={20} /> Place on Hold
              </h3>
              <button onClick={() => setShowHoldModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
                Explain why this packing task is being put on hold.
            </p>
            <textarea 
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                style={{ ...inputStyle, minHeight: '100px', marginBottom: '20px' }}
                placeholder="e.g. Item damaged in warehouse, Waiting for missing components..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowHoldModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={handleHold} disabled={isUpdating || !holdReason.trim()} style={{ ...btnStyle, backgroundColor: '#f59e0b', color: 'white' }}>
                {isUpdating ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Hold'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '450px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={20} /> Reject MOC
              </h3>
              <button onClick={() => setShowRejectModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
                Why are you rejecting this MOC? This will remove it from the packing queue.
            </p>
            <textarea 
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                style={{ ...inputStyle, minHeight: '100px', marginBottom: '20px', borderColor: '#fee2e2' }}
                placeholder="e.g. Inventory mismatch, Order cancelled by customer..."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowRejectModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={handleReject} disabled={isUpdating || !rejectReason.trim()} style={{ ...btnStyle, backgroundColor: '#dc2626', color: 'white' }}>
                {isUpdating ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FOOTER TIMELINE */}
      {!loading && moc && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1100px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline db={db} orderData={order} dispatchesData={dispatches} dispatchData={moc} />
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const previewContainerStyle = { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const btnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' };
const confirmBtnStyle = { ...btnStyle, backgroundColor: '#059669', color: 'white' };
const saveBtnStyle = { ...btnStyle, backgroundColor: '#2563eb', color: 'white' };
const secondaryBtnStyle = { ...btnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' };
const cancelBtnStyle = { ...btnStyle, backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' };
const lockedBadgeStyle = { backgroundColor: '#f0fdf4', color: '#16a34a', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '5px' };
const scheduleSummaryGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' };
const scheduleInfoCardStyle = { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' };
const schedulePrimaryValueStyle = { fontSize: '22px', fontWeight: '800', color: '#0f172a' };
const scheduleSecondaryValueStyle = { fontSize: '14px', fontWeight: '700', color: '#334155' };
const scheduleRemarksCardStyle = { backgroundColor: '#ffffff', border: '1px solid #dbeafe', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' };
const proofFileLinkStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', textDecoration: 'none', fontSize: '13px', fontWeight: '600', minWidth: 0 };
const priorityPillStyle = (priority = 'Medium') => {
  const colors = {
    High: { backgroundColor: '#fee2e2', color: '#b91c1c', borderColor: '#fecaca' },
    Medium: { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' },
    Low: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }
  };

  return {
    ...(colors[priority] || colors.Medium),
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    padding: '6px 12px',
    borderRadius: '999px',
    border: `1px solid ${(colors[priority] || colors.Medium).borderColor}`,
    fontSize: '13px',
    fontWeight: '800'
  };
};

// PDF PREVIEW STYLES
const pdfContainerStyle = { padding: '20px 40px', color: '#000' };

export default PackingDetail;
